import { useEffect, useState } from "react";
import { Lock, Unlock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useUpdateFolder } from "@/hooks/use-folders";
import { FeatureLock } from "@/components/billing/feature-lock";
import type { Folder } from "@/lib/api-types";

type Props = {
  folder: Folder;
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Folder settings — rename + toggle restricted. Member management (add/remove
 * users when restricted) is added in Task 4. Save button issues a single PATCH
 * with only the fields that actually changed.
 *
 * Restricted toggle semantics (locked with user 2026-07-14):
 *   - Turning ON does NOT auto-populate folder_editors. Anyone in EDIT /
 *     EDIT_SHARE who could enter before loses access instantly and must be
 *     re-invited. ADMIN + FULL_ACCESS keep access via workspace bypass.
 *   - Turning OFF leaves any prior members in folder_editors intact (no-op
 *     effect while unrestricted, preserved for the next flip).
 */
export function FolderSettingsDialog({ folder, projectId, open, onOpenChange }: Props) {
  const [name, setName] = useState(folder.name);
  const [restricted, setRestricted] = useState(folder.restricted);
  const update = useUpdateFolder();

  useEffect(() => {
    if (open) {
      setName(folder.name);
      setRestricted(folder.restricted);
    }
  }, [open, folder.name, folder.restricted]);

  const nameTrimmed = name.trim();
  const dirty =
    (nameTrimmed && nameTrimmed !== folder.name) || restricted !== folder.restricted;

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!dirty || !nameTrimmed) return;
    await update.mutateAsync({
      folderId: folder.id,
      name: nameTrimmed !== folder.name ? nameTrimmed : undefined,
      restricted: restricted !== folder.restricted ? restricted : undefined,
      projectId,
      parentFolderId: folder.parentFolderId,
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <form onSubmit={handleSave} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Folder settings</DialogTitle>
            <DialogDescription>
              Rename the folder and control who can enter it.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            <label htmlFor="folder-name" className="text-sm font-medium text-foreground">
              Name
            </label>
            <Input
              id="folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Untitled Folder"
            />
          </div>

          <FeatureLock feature="restrictedAccess">
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-md bg-secondary text-muted-foreground">
                  {restricted ? (
                    <Lock className="h-4 w-4" strokeWidth={1.75} />
                  ) : (
                    <Unlock className="h-4 w-4" strokeWidth={1.75} />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">Make Restricted</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    When on, only Workspace Admin and Full Access users can enter this
                    folder. Everyone else needs to be invited separately.
                  </p>
                  {restricted && !folder.restricted && (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-500">
                      Current Edit and Edit & Share users lose access when you save
                      unless you invite them explicitly.
                    </p>
                  )}
                </div>
              </div>
              <Switch
                checked={restricted}
                onCheckedChange={setRestricted}
                aria-label="Toggle restricted"
              />
            </div>
          </FeatureLock>

          {restricted && (
            <div className="rounded-lg border border-dashed border-border px-4 py-4 text-center text-xs text-muted-foreground">
              Open the folder to invite specific members via the Invite button
              in the top bar. Workspace Admin and Full Access users always have
              access.
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!dirty || !nameTrimmed || update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
